import styled, { keyframes } from "styled-components";

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 22px 22px;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  isolation: isolate;
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const PromptModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const PromptModalTitle = styled.h3`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 20px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;

  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const PromptCloseButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;

    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ModalMessage = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
`;

export const PromptModalButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
`;

export const PromptSubmitButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow: var(--shadow-cta);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;
